"use client";
import { useState } from "react";

export type DateRange = { desde: string; hasta: string };
// La clave del preset activo viaja junto al rango para que el componente padre
// pueda derivar etiquetas sin necesidad de inferir el preset de las fechas.
export type RangeWithPreset = { desde: string; hasta: string; presetKey: string };

type Preset = { key: string; label: string; range: () => DateRange };

function pad(n: number) { return String(n).padStart(2, "0"); }
function toISO(d: Date) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function addDays(d: Date, n: number) {
  const r = new Date(d); r.setDate(r.getDate() + n); return r;
}
function startOfWeek(d: Date) {
  const r = new Date(d);
  const day = r.getDay() || 7;
  r.setDate(r.getDate() - day + 1);
  return r;
}
function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function startOfYear(d: Date) {
  return new Date(d.getFullYear(), 0, 1);
}

export function getPresets(): Preset[] {
  return [
    {
      key: "hoy",
      label: "Hoy",
      range: () => { const t = toISO(new Date()); return { desde: t, hasta: t }; },
    },
    {
      key: "semana",
      label: "Esta semana",
      range: () => {
        const now = new Date();
        return { desde: toISO(startOfWeek(now)), hasta: toISO(now) };
      },
    },
    {
      key: "mes",
      label: "Este mes",
      range: () => {
        const now = new Date();
        return { desde: toISO(startOfMonth(now)), hasta: toISO(now) };
      },
    },
    {
      key: "mes_pasado",
      label: "Mes pasado",
      range: () => {
        const now = new Date();
        const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        return { desde: toISO(first), hasta: toISO(endOfMonth(first)) };
      },
    },
    {
      key: "anio",
      label: "Este año",
      range: () => {
        const now = new Date();
        return { desde: toISO(startOfYear(now)), hasta: toISO(now) };
      },
    },
  ];
}

export function defaultRange(): RangeWithPreset {
  const r = getPresets().find(p => p.key === "mes")!.range();
  return { ...r, presetKey: "mes" };
}

function fmtDate(iso: string) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y.slice(2)}`;
}

export function comparacionLabel(range: DateRange) {
  const desde = new Date(range.desde + "T00:00:00");
  const hasta  = new Date(range.hasta + "T23:59:59");
  const durDias = Math.ceil((hasta.getTime() - desde.getTime()) / 86400000);
  const prevHasta = addDays(desde, -1);
  const prevDesde = addDays(desde, -durDias);
  return `vs ${fmtDate(toISO(prevDesde))}–${fmtDate(toISO(prevHasta))}`;
}

interface Props {
  value: RangeWithPreset;
  onChange: (r: RangeWithPreset) => void;
}

export default function DateRangePicker({ value, onChange }: Props) {
  const presets = getPresets();
  const [showCustom, setShowCustom] = useState(false);
  const [draft, setDraft] = useState({ desde: value.desde, hasta: value.hasta });

  // El preset activo viene del estado explícito (presetKey), NO inferido de las fechas.
  // Esto evita que "Este mes" (1–5 jun) sea confundido con "Esta semana" (lun–jue jun)
  // cuando ambos rangos coinciden en fechas.
  const activePresetKey = value.presetKey;

  const applyCustom = () => {
    if (draft.desde && draft.hasta && draft.desde <= draft.hasta) {
      onChange({ ...draft, presetKey: "custom" });
      setShowCustom(false);
    }
  };

  const isCustom = activePresetKey === "custom";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
        {/* Pills presets */}
        <div style={{ display: "inline-flex", background: "var(--panel2, rgba(255,255,255,.06))", borderRadius: "10px", padding: "3px", gap: "2px" }}>
          {presets.map(p => {
            const isActive = activePresetKey === p.key;
            return (
              <button
                key={p.key}
                onClick={() => {
                  onChange({ ...p.range(), presetKey: p.key });
                  setShowCustom(false);
                }}
                style={{
                  padding: "5px 12px", borderRadius: "7px", border: "none",
                  fontSize: "12px", fontWeight: isActive ? 700 : 400,
                  cursor: "pointer", transition: "all 150ms", fontFamily: "inherit",
                  background: isActive ? "var(--acc, #3b82f6)" : "transparent",
                  color: isActive ? "#fff" : "var(--tx3, var(--text-muted))",
                  whiteSpace: "nowrap",
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>

        {/* Rango libre toggle */}
        <button
          onClick={() => setShowCustom(s => !s)}
          style={{
            padding: "5px 12px", borderRadius: "7px",
            border: `1px solid ${isCustom || showCustom ? "var(--acc, #3b82f6)" : "var(--bd, var(--border))"}`,
            fontSize: "12px", cursor: "pointer", fontFamily: "inherit",
            background: isCustom ? "var(--acc, #3b82f6)" : "transparent",
            color: isCustom ? "#fff" : showCustom ? "var(--acc, #3b82f6)" : "var(--tx3, var(--text-muted))",
            fontWeight: isCustom ? 700 : 400,
          }}
        >
          {isCustom ? `${fmtDate(value.desde)} → ${fmtDate(value.hasta)}` : "Rango libre"}
        </button>
      </div>

      {/* Panel rango libre */}
      {showCustom && (
        <div style={{
          display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap",
          padding: "10px 14px", background: "var(--panel, var(--bg-card))",
          border: "1px solid var(--bd, var(--border))", borderRadius: "10px",
        }}>
          <span style={{ fontSize: "12px", color: "var(--tx3, var(--text-muted))" }}>Desde</span>
          <input
            type="date"
            value={draft.desde}
            onChange={e => setDraft(d => ({ ...d, desde: e.target.value }))}
            style={{ padding: "5px 8px", borderRadius: "7px", border: "1px solid var(--bd, var(--border))", background: "var(--panel2, var(--bg-muted))", color: "var(--tx, var(--text))", fontSize: "12px", fontFamily: "inherit" }}
          />
          <span style={{ fontSize: "12px", color: "var(--tx3, var(--text-muted))" }}>hasta</span>
          <input
            type="date"
            value={draft.hasta}
            onChange={e => setDraft(d => ({ ...d, hasta: e.target.value }))}
            style={{ padding: "5px 8px", borderRadius: "7px", border: "1px solid var(--bd, var(--border))", background: "var(--panel2, var(--bg-muted))", color: "var(--tx, var(--text))", fontSize: "12px", fontFamily: "inherit" }}
          />
          <button
            onClick={applyCustom}
            disabled={!draft.desde || !draft.hasta || draft.desde > draft.hasta}
            style={{
              padding: "5px 14px", borderRadius: "7px", border: "none",
              background: "var(--acc, #3b82f6)", color: "#fff",
              fontSize: "12px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
              opacity: (!draft.desde || !draft.hasta || draft.desde > draft.hasta) ? 0.4 : 1,
            }}
          >
            Aplicar
          </button>
        </div>
      )}

      {/* Texto de comparación */}
      <div style={{ fontSize: "11px", color: "var(--tx3, var(--text-light))" }}>
        {comparacionLabel(value)}
      </div>
    </div>
  );
}
